/**
 * Analyses complémentaires du gestionnaire de switch (pures, sans effet de bord) :
 * croisement avec le registre (types, non déclarés) et détection de clés proches.
 */

const levenshtein = (a, b) => {
  const m = a.length
  const n = b.length
  if (!m) return n
  if (!n) return m
  let prev = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    const cur = [i]
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
    }
    prev = cur
  }
  return prev[n]
}

const normalizeKey = k => String(k || '').toLowerCase().replace(/[^a-z0-9]/g, '')

/** Retourne une Map clé → clés très proches (fautes de frappe probables). */
export const findNearDuplicates = keys => {
  const map = new Map()
  const add = (a, b) => {
    if (!map.has(a)) map.set(a, [])
    if (!map.get(a).includes(b)) map.get(a).push(b)
  }
  for (let i = 0; i < keys.length; i++) {
    for (let j = i + 1; j < keys.length; j++) {
      const a = keys[i]
      const b = keys[j]
      if (!a || !b || a === b) continue
      const near = normalizeKey(a) === normalizeKey(b) || (Math.max(a.length, b.length) >= 5 && levenshtein(a, b) <= 2)
      if (near) {
        add(a, b)
        add(b, a)
      }
    }
  }
  return map
}

/** Enrichit chaque switch de la vue d'ensemble avec les infos du registre + alertes. */
export const analyzeSwitches = (switches, registry) => {
  const byKey = {}
  ;(registry || []).forEach(e => e.key && (byKey[e.key] = e))
  const near = findNearDuplicates((switches || []).map(s => s.key))

  return (switches || []).map(s => {
    const entry = byKey[s.key]
    const warnings = [...(s.warnings || [])]
    const used = [...(s.writtenValues || []), ...(s.testedValues || [])].filter(v => v !== '' && !/^[-+]\d+$/.test(String(v)))

    if (!entry) warnings.push('unregistered')
    else if (entry.type === 'enum' && (entry.values || []).length) {
      const allowed = new Set(entry.values)
      if (used.some(v => !allowed.has(v))) warnings.push('value-not-in-enum')
    } else if (entry.type === 'number') {
      if (used.some(v => isNaN(Number(v)))) warnings.push('not-numeric')
    }

    const nearDuplicates = near.get(s.key) || []
    if (nearDuplicates.length) warnings.push('near-duplicate')

    return { ...s, entry, registered: !!entry, warnings, nearDuplicates }
  })
}

/** Devine un type de switch à partir des valeurs observées (import au registre). */
export const inferType = (writtenValues = []) => {
  const vals = [...new Set(writtenValues.filter(v => v !== '' && !/^[-+]\d+$/.test(String(v))))]
  if (vals.length === 0) return { type: 'string', values: [] }
  if (vals.every(v => !isNaN(Number(v)))) return { type: 'number', values: [] }
  return { type: 'enum', values: vals }
}

/** Problèmes de saisie du registre, par entrée (même index que `registry`). */
export const validateRegistry = registry => {
  const counts = {}
  ;(registry || []).forEach(e => e.key && (counts[e.key] = (counts[e.key] || 0) + 1))
  return (registry || []).map(e => {
    const issues = []
    if (!e.key || !e.key.trim()) issues.push('empty-key')
    else if (counts[e.key] > 1) issues.push('duplicate-key')
    if (e.type === 'enum' && !(e.values || []).filter(v => v !== '').length) issues.push('enum-empty')
    return issues
  })
}
